import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useCallback, useEffect } from 'react';
import { IMFPPStore } from '../../stores';
import { useSearchParams } from 'react-router-dom';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useRefetchOnVisibilityAndRoute } from '../tools/use-refetch-on-visibility-and-route';

const STALE_THRESHOLD_MS = 5 * 10 * 1000;

export function useMyLinksList() {
	const { linksStore, publisherStore } = useStores<IMFPPStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const [searchParams, setSearchParams] = useSearchParams();
	const accountId = searchParams.get('accountId');
	// Read during render so observer (e.g. MyLinks) re-renders when publishers load; effect with only [accountId] misses that.
	const fallbackAccountId = publisherStore.getListStore().getData()?.[0]?.id;

	useEffect(() => {
		linksStore.ensureFiltersForRoute(currentRouteWithoutSpecialChars);
	}, [currentRouteWithoutSpecialChars, linksStore]);

	useEffect(() => {
		if (accountId) {
			linksStore.fetchMyLinks(accountId);
		} else if (fallbackAccountId) {
			setSearchParams({ accountId: `${fallbackAccountId}` });
		}
	}, [accountId, fallbackAccountId, linksStore, setSearchParams]);

	const refetchMyLinks = useCallback(() => {
		if (accountId) {
			void linksStore.fetchMyLinks(accountId);
		}
	}, [accountId, linksStore]);

	/**
	 * Background refresh: only fires when data is older than {@link STALE_THRESHOLD_MS}.
	 * Does not set a loading state so there is no visual flicker.
	 */
	const silentRefetch = useCallback(() => {
		if (!accountId) {
			return;
		}
		if (Date.now() - linksStore.lastFetchedAt < STALE_THRESHOLD_MS) {
			return;
		}
		void linksStore.silentFetchMyLinks(accountId);
	}, [accountId, linksStore]);

	/**
	 * When the tab becomes visible again, just reset the stale timer so the next
	 * interval tick decides whether a real refetch is needed — avoids an immediate
	 * full reload every time the user alt-tabs back.
	 */
	const touchTimestamp = useCallback(() => {
		if (linksStore.lastFetchedAt > 0) {
			linksStore.setLastFetchedAt(Date.now());
		}
	}, [linksStore]);

	useRefetchOnVisibilityAndRoute(touchTimestamp, Boolean(accountId));

	// Periodic silent refresh — runs silentRefetch every STALE_THRESHOLD_MS.
	useEffect(() => {
		if (!accountId) {
			return;
		}
		const id = setInterval(silentRefetch, STALE_THRESHOLD_MS);
		return () => clearInterval(id);
	}, [accountId, silentRefetch]);

	const numericAccountId = accountId ? Number(accountId) : undefined;

	return {
		error: linksStore.error,
		isLoading: linksStore.isLoading,
		data: linksStore.getLinks(),
		accountId: numericAccountId !== undefined && Number.isFinite(numericAccountId) ? numericAccountId : undefined,
		refetchMyLinks,
	};
}
